import * as React from "react";

import { Flux } from "VersionControl/Scenarios/PullRequestDetail/View/PullRequestDetail.Flux";

// stores
import { IPullRequest } from "VersionControl/Scripts/Stores/PullRequestReview/PullRequestDetailStore";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";

// contracts
import * as IdentityImage from "Presentation/Scripts/TFS/Components/IdentityImage";
import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import * as VCResources from "VersionControl/Scripts/Resources/TFS.Resources.VersionControl";
import * as Utils_String from "VSS/Utils/String";
import { IdentityRef } from "VSS/WebApi/Contracts";
import { GitPullRequestMergeStrategy } from "TFS/VersionControl/Contracts";
import { mergeStrategyDetails } from "VersionControl/Scripts/MergeStrategyDetails";
import { FeatureAvailabilityFlags } from "Presentation/Scripts/TFS/Generated/TFS.Server.WebAccess.Constants";
import { PullRequestPolicyTypeIds } from "VersionControl/Scenarios/PullRequestDetail/Policy/ClientPolicyEvaluation";

// components
import { DefaultButton } from "OfficeFabric/Button";
import { List } from "OfficeFabric/List";
import { TooltipHost, DirectionalHint, TooltipOverflowMode } from "VSSUI/Tooltip";
import * as Activity from "VersionControl/Scripts/Components/PullRequestReview/Activities/Activity";
import { PullRequestCallout } from "VersionControl/Scripts/Components/PullRequestReview/PullRequestCallout";

import { css } from "VSSUI/Util";
import { FormattedComponent } from "VersionControl/Scripts/Utils/Format";
import { PullRequestVoteStatus } from "VersionControl/Scripts/Utils/ReviewerUtils";

import "VSS/LoaderPlugins/Css!VersionControl/AutoCompleteCallout";

export interface AutoCompleteCalloutContainerProps {
    pullRequest: IPullRequest;
    tfsContext: TfsContext;
    autoCompleter: IdentityRef;
    hasPermissionToCancelAutoComplete: boolean;
    cancelAutoComplete(): void;
}

interface ClientPolicyEvaluationError {
    displayName: string;
    displayText: string;
}

export interface AutoCompleteCalloutState {
    blockingEvaluations: ClientPolicyEvaluationError[];
}

export class AutoCompleteCalloutContainer extends React.Component<AutoCompleteCalloutContainerProps, AutoCompleteCalloutState> {
    constructor(props: AutoCompleteCalloutComponentProps) {
        super(props);
        this.state = this._getStateFromStore();
    }

    public componentDidMount(): void {
        Flux.instance().storesHub.autoCompleteStore.addChangedListener(this._onChange);
        Flux.instance().storesHub.clientPolicyEvaluationStore.addChangedListener(this._onChange);

        if (FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.SourceControlGitPullRequestsEnforceAdHocRequiredReviewers, false)) {
            Flux.instance().storesHub.reviewersStore.addChangedListener(this._onChange);
        }
    }

    public componentWillUnmount(): void {
        Flux.instance().storesHub.autoCompleteStore.removeChangedListener(this._onChange);
        Flux.instance().storesHub.clientPolicyEvaluationStore.removeChangedListener(this._onChange);

        if (FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.SourceControlGitPullRequestsEnforceAdHocRequiredReviewers, false)) {
            Flux.instance().storesHub.reviewersStore.removeChangedListener(this._onChange);
        }
    }

    private _onChange = () => {
        this.setState(this._getStateFromStore());
    }

    private _getStateFromStore(): AutoCompleteCalloutState {
        const policyState = Flux.instance().storesHub.clientPolicyEvaluationStore.state;
        const clientPolicyEvaluations = policyState.clientPolicyEvaluations;
        const storeBlockingEvaluations = Flux.instance().storesHub.autoCompleteStore.getClientPolicyEvaluationsBlockingAutoComplete(clientPolicyEvaluations);
        const blockingEvaluations = storeBlockingEvaluations
            ? storeBlockingEvaluations.map(evaluation => evaluation.policyEvaluation as ClientPolicyEvaluationError)
            : [];

        const hasBlockingRequiredReviewerPolicy = storeBlockingEvaluations &&
            storeBlockingEvaluations.some(evaluation => evaluation.policyEvaluation.policyType.id === PullRequestPolicyTypeIds.RequiredReviewersPolicy);

        // This is needed when server is configured to enforce required reviewers which have no policy.
        if (!hasBlockingRequiredReviewerPolicy
            && FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.SourceControlGitPullRequestsEnforceAdHocRequiredReviewers, false)
        ) {
            const reviewers = Flux.instance().storesHub.reviewersStore.getReviewers();

            if (reviewers.some(r => r.identity.isRequired && !(r.identity.vote > PullRequestVoteStatus.NONE))) {
                blockingEvaluations.push({
                    displayName: VCResources.PullRequest_RequiredReviewers,
                    displayText: VCResources.PullRequest_RequiredReviewer_NotAllApproved
                });
            }
        }

        const selectedMergeStrategy = mergeStrategyDetails[this.props.pullRequest.mergeStrategy] || mergeStrategyDetails[GitPullRequestMergeStrategy.NoFastForward];
        const isMergeStrategyAllowed = selectedMergeStrategy.isAllowedByPolicy(policyState.mergePolicySetting);
        if (!isMergeStrategyAllowed) {
            blockingEvaluations.push({
                displayName: VCResources.PullRequest_AutoCompleteMergeErrorName,
                displayText: Utils_String.format(VCResources.PullRequest_AutoCompleteMergeErrorText, selectedMergeStrategy.text)
            });
        }

        return { blockingEvaluations };
    }

    public render(): JSX.Element {
        const calloutProps = {
            ...this.props,
            blockingEvaluations: this.state.blockingEvaluations
        };

        return <AutoCompleteCallout {...calloutProps} />;
    }
}

export interface AutoCompleteCalloutComponentProps {
    pullRequest: IPullRequest;
    tfsContext: TfsContext;
    autoCompleter: IdentityRef;
    hasPermissionToCancelAutoComplete: boolean;
    cancelAutoComplete(): void;
    blockingEvaluations: ClientPolicyEvaluationError[];
}

export class AutoCompleteCallout extends React.Component<AutoCompleteCalloutComponentProps, {}> {

    public render(): JSX.Element {
        const { pullRequest, tfsContext, autoCompleter, blockingEvaluations } = this.props;

        const autoCompleterDisplayName =
            (pullRequest.autoCompleteSetBy.displayName)
            // This shouldn't happen
            || VCResources.PullRequest_PullRequestDetailsStatusUnKnown;

        const autoCompleteStatus: string = blockingEvaluations && blockingEvaluations.length > 0 ?
            Utils_String.format(VCResources.PullRequest_CallToAction_AutoComplete_SetBy_WithCriteria, autoCompleterDisplayName)
            : Utils_String.format(VCResources.PullRequest_CallToAction_AutoComplete_SetBy, autoCompleterDisplayName);

        const completerImage = Activity.tfIdImage(tfsContext, autoCompleter, IdentityImage.imageSizeSmall);

        const targetBranchHtmlEscaped = Utils_String.htmlEncode(pullRequest.targetFriendlyName);
        const sourceBranchHtmlEscaped = Utils_String.htmlEncode(pullRequest.sourceFriendlyName);

        const completionOptions = pullRequest.pullRequestContract().completionOptions;

        let futureMergeStrategy: string;

        switch (pullRequest.mergeStrategy) {
            case GitPullRequestMergeStrategy.NoFastForward:
            default:
                futureMergeStrategy = VCResources.MergedLowerCase;
                break;

            case GitPullRequestMergeStrategy.Squash:
                futureMergeStrategy = VCResources.SquashedLowerCase;
                break;

            case GitPullRequestMergeStrategy.Rebase:
                futureMergeStrategy = VCResources.RebasedLowerCase;
                break;

            case GitPullRequestMergeStrategy.RebaseMerge:
                futureMergeStrategy = VCResources.RebaseMergedLowerCase;
                break;
        }

        const willDelete: boolean = completionOptions && completionOptions.deleteSourceBranch;
        const willTransition: boolean = completionOptions && completionOptions.transitionWorkItems;

        return (
            <PullRequestCallout buttons={[this._cancelAutoCompleteButton()]} className="auto-complete">
                <div key="callout" className="vc-pullrequest-callout-container">
                    <div className="vc-pullrequest-callout-text" key="callout">
                        <span className="space-right">{completerImage}</span>
                        {autoCompleteStatus}
                    </div>
                    <List
                        key="policies-list"
                        className="policies-list"
                        items={blockingEvaluations}
                        onRenderCell={this._onRenderCell} />
                    <FormattedComponent
                        format={VCResources.PullRequest_CallToAction_AutoCompletion_Options}
                        className="callout-details"
                        elementType="div"
                        key="details">
                        {[
                            <strong key="squash">{futureMergeStrategy}</strong>,
                            targetBranchHtmlEscaped,
                            sourceBranchHtmlEscaped,
                            <strong key="delete">{willDelete ? VCResources.DeletedLowerCase : VCResources.RetainedLowerCase}</strong>,
                            <strong key="transition">{willTransition ? VCResources.CompletedLowerCase : VCResources.UnchangedLowerCase}</strong>,
                        ]}
                    </FormattedComponent>
                </div>
            </PullRequestCallout>
        );
    }

    private _cancelAutoCompleteButton(): JSX.Element {
        return this.props.hasPermissionToCancelAutoComplete &&
            <DefaultButton
                key="cancelAutoComplete"
                onClick={this.props.cancelAutoComplete}>
                {VCResources.CancelAutoComplete}
            </DefaultButton>;
    }

    private _onRenderCell = (policyEvaluation: ClientPolicyEvaluationError): JSX.Element => {
        const statusText = policyEvaluation.displayText;
        const displayName = policyEvaluation.displayName;
        return <div className="policies-list-item">
            <TooltipHost
                hostClassName={css("policy-name", "ellide-overflow")}
                content={displayName}
                directionalHint={DirectionalHint.bottomCenter}
                overflowMode={TooltipOverflowMode.Self}>
                <span>{displayName}</span>
            </TooltipHost>
            <TooltipHost
                hostClassName={css("policy-status", "ellide-overflow")}
                content={statusText}
                directionalHint={DirectionalHint.bottomCenter}
                overflowMode={TooltipOverflowMode.Self}>
                <span>{statusText}</span>
            </TooltipHost>
        </div>;
    }
}
